import { useQuery } from "@tanstack/react-query";

import axiosClient from "./apiClient";

/**
 * The roles an admin may assign, with the Auth0 ids needed to assign them.
 *
 * Served by `GET Auth0/v1/roles` (REQ-ACL-1). It exists because the role endpoints
 * take Auth0 role ids and nothing previously told a client what they were -- which
 * is why the phone app hardcodes four and maps names back by substring, collapsing
 * CTS-Admin, FamilyAdmin and OrgAdmin onto one label. This client must not repeat
 * that: no role name or `rol_` literal belongs anywhere but the API's response.
 */
export type RoleCatalogueEntry = {
  id: string;
  name: string;
  summary: string;
  /**
   * What to call this role on screen: "Administrator", "Manager", "User"...
   *
   * The API decides, because the word depends on the tenant -- a household says
   * "Member" where an organization says "User" for the same tier -- and because
   * `name` above is an authorization identifier that happens to be unreadable.
   */
  label: string;
  /** "Administrator" | "Manager" | "User" | "None" */
  tier: string;
  /** "Organization" | "Family" | "Internal" | "Unused" */
  kind: string;
  capabilities: string[];
  /** False for internal, family and unused roles. */
  assignable_from_dashboard: boolean;
};

export type RoleCatalogue = {
  roles: RoleCatalogueEntry[];
  /** Roles Auth0 has that the API cannot map. They confer nothing. */
  unmapped_in_api: string[];
  /** Roles the API maps that Auth0 lacks. Should always be empty. */
  missing_in_auth0: string[];
};

export async function getRoleCatalogue(): Promise<RoleCatalogue> {
  const res = await axiosClient.get<RoleCatalogue>("Auth0/v1/roles");
  return res.data;
}

export const ROLES_QUERY_KEY = ["roles", "catalogue"] as const;

/**
 * The role catalogue.
 *
 * `staleTime: Infinity` -- roles are defined in the Auth0 dashboard, not by anything
 * happening in this app, so re-fetching while someone edits a picker would only
 * risk changing the options under them.
 */
export function useRoleCatalogue() {
  return useQuery({
    queryKey: ROLES_QUERY_KEY,
    queryFn: getRoleCatalogue,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** The roles a tenant's own admin may hand out. */
export function assignableRoles(catalogue: RoleCatalogue | undefined) {
  return (catalogue?.roles ?? []).filter((r) => r.assignable_from_dashboard);
}

/**
 * Which tier the access editor should follow for one person.
 *
 * Pure, exported and tested, because getting it wrong fails OPEN: the wider
 * editor offers whole properties to somebody the API will hold to a single
 * sensor (REQ-ACL-15), and an admin only finds out when the save is refused.
 *
 * The SELECTED role wins when there is one, so picking "User" switches the editor
 * before the role is saved -- which it has to, since a User's grant must already
 * name one sensor before the demote is accepted.
 *
 * With nothing selected the answer is the API's own fold, `member.tier`, and NOT a
 * lookup in the assignable catalogue. That lookup failed open twice:
 *
 *  - the catalogue is a separate query, so while it was pending or failed the
 *    assignable list was empty and nothing matched;
 *  - `assignableRoles` drops every role a tenant admin cannot hand out, so a
 *    person holding a family or internal role matched nothing even once it had
 *    loaded. Their tier is still User.
 *
 * Roles are matched on NAME. The label is a word for a screen, and renaming one
 * must never change who is held to which rule.
 */
export function tierForEditor({
  selectedRoleId,
  assignable,
  heldRoleNames,
  memberTier,
}: {
  selectedRoleId: string | null;
  assignable: RoleCatalogueEntry[];
  heldRoleNames: (string | undefined)[];
  memberTier: string | null | undefined;
}): string {
  const selected = assignable.find((r) => r.id === selectedRoleId);
  if (selected) return selected.tier;

  const fromCatalogue = assignable.find((r) =>
    heldRoleNames.includes(r.name)
  )?.tier;

  return fromCatalogue ?? memberTier ?? "";
}

/**
 * Seniority ordering, mirroring the API's `RoleTier`.
 *
 * A client-side copy of a server-side ordering, which is worth naming as such.
 * It is used for presentation decisions only -- sorting the People table, and
 * narrowing the invite picker below -- and **never** to decide what anybody may
 * do. The API re-derives the comparison on every write (REQ-ACL-14), so a wrong
 * answer here shows the wrong options and then gets refused; it cannot grant
 * anything.
 */
export const TIER_RANK: Record<string, number> = {
  Administrator: 3,
  Manager: 2,
  User: 1,
  None: 0,
};

/**
 * The viewer's own tier, derived by joining their roles against the catalogue.
 *
 * `/me` reports role NAMES and no tier, and the dashboard may hold no role-name
 * literal -- so neither side alone can answer this. Joining them does: both the
 * names and the tiers come from the API, and this only matches one against the
 * other.
 *
 * Returns "None" when the catalogue has not loaded, which is the closed answer:
 * it narrows the invite picker to nothing rather than offering everything while
 * the tier is unknown.
 */
export function viewerTier(
  roleNames: (string | undefined)[],
  catalogue: RoleCatalogue | undefined
): string {
  const tiers = (catalogue?.roles ?? [])
    .filter((r) => roleNames.includes(r.name))
    .map((r) => TIER_RANK[r.tier] ?? 0);

  const best = tiers.length > 0 ? Math.max(...tiers) : 0;

  return (
    Object.keys(TIER_RANK).find((tier) => TIER_RANK[tier] === best) ?? "None"
  );
}

/**
 * The roles this viewer may hand out, narrowed by their own tier.
 *
 * Mirrors `RoleCapabilities.MayAssign`, including its one special case: an
 * **Administrator may assign any role, their own tier included**, so that an admin
 * can appoint a successor. Everybody else may assign strictly below themselves,
 * which is what stops a Manager creating another Manager (REQ-ACL-14).
 *
 * Presentation only. The server refuses the same thing independently, and the
 * invite modal surfaces that refusal rather than assuming this list was right --
 * if the two ever disagree, the API wins and the admin is told why.
 */
export function invitableRoles(
  catalogue: RoleCatalogue | undefined,
  callerTier: string
): RoleCatalogueEntry[] {
  const assignable = assignableRoles(catalogue);

  if (callerTier === "Administrator") return assignable;

  const callerRank = TIER_RANK[callerTier] ?? 0;

  return assignable.filter((r) => (TIER_RANK[r.tier] ?? 0) < callerRank);
}
